import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toastr } from "react-redux-toastr";
import { Modal } from "components";
import { client_v2, getApiErrorMessage } from "utils/api";

const getStockStatusColor = (diff) => {
  if (diff > 0) {
    return { color: "#0abb87" };
  }
  if (diff < 0) {
    return { color: "#fd397a" };
  }

  return {};
};

const BalanceWarehouseDiff = ({
  productId,
  mainWarehouseDiff,
  shipmentWarehouseDiff,
}) => {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const hasOppositeSign =
    (mainWarehouseDiff > 0 && shipmentWarehouseDiff < 0) ||
    (mainWarehouseDiff < 0 && shipmentWarehouseDiff > 0);

  if (!hasOppositeSign) return null;

  const { isLoading: isConfirming, mutate: confirmMutate } = useMutation(
    () =>
      client_v2.post(`/products/${productId}/balance_stock_diff_with_mmlog`),
    {
      onSuccess: () => {
        toastr.success("Sukces", "Wyrównano różnice magazynowe");
        setPreview(null);
        queryClient.invalidateQueries(["warehouse_location_diffs"]);
      },
      onError: (err) => {
        toastr.error("Błąd", getApiErrorMessage(err));
        setPreview(null);
      },
    },
  );

  const handleClick = async () => {
    setIsLoadingPreview(true);
    try {
      const res = await client_v2.get(
        `/products/${productId}/balance_stock_diff_with_mmlog_preview`,
      );
      setPreview(res?.data);
    } catch (err) {
      toastr.error("Błąd", getApiErrorMessage(err));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-blue"
        onClick={handleClick}
        disabled={isLoadingPreview}
      >
        {isLoadingPreview ? "..." : "Wyrównaj różnice"}
      </button>
      {preview && (
        <Modal
          header={{ title: "Potwierdzenie wyrównania różnic magazynowych" }}
          handleClose={() => setPreview(null)}
          footer={
            <div className="df h-end gap-10">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setPreview(null)}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn btn-sm btn-green"
                onClick={() => confirmMutate()}
                disabled={isConfirming || preview.quantity === 0}
              >
                {isConfirming ? "Wykonuję..." : "Potwierdź"}
              </button>
            </div>
          }
        >
          {preview.quantity === 0 ? (
            <p className="m-b-10 text-danger">
              Nie można przenieść przez MM – stan realny na magazynie źródłowym
              wynosi 0. Wykonaj przeniesienie fizyczne (
              {preview.quantity_to_move_physically} szt.) i potwierdź w
              Navigatorze.
            </p>
          ) : (
            <h4 className="m-b-10">Zostanie utworzona MM:</h4>
          )}
          <p className="m-b-5">
            {preview.quantity === 0
              ? `${preview.physical_source_warehouse_name} → ${preview.physical_target_warehouse_name}`
              : `${preview.source_warehouse_name} → ${preview.target_warehouse_name}`}
          </p>
          <p className="m-b-5">
            <strong>{preview.sku}</strong>, {preview.product_name}
          </p>
          <p className="m-b-5">
            Ilość przesuwanych sztuk:{" "}
            <strong>
              {preview.quantity === 0
                ? preview.quantity_to_move_physically
                : preview.quantity}
            </strong>
          </p>
          <p>
            Różnica na <strong>{preview.main_warehouse_name}</strong> zmieni się
            z{" "}
            <strong style={getStockStatusColor(preview.main_diff_before)}>
              {preview.main_diff_before}
            </strong>{" "}
            →{" "}
            <strong style={getStockStatusColor(preview.main_diff_after)}>
              {preview.main_diff_after}
            </strong>
          </p>
          <p>
            Różnica na <strong>{preview.shipping_warehouse_name}</strong> zmieni
            się z{" "}
            <strong style={getStockStatusColor(preview.shipping_diff_before)}>
              {preview.shipping_diff_before}
            </strong>{" "}
            →{" "}
            <strong style={getStockStatusColor(preview.shipping_diff_after)}>
              {preview.shipping_diff_after}
            </strong>
          </p>
        </Modal>
      )}
    </>
  );
};

export default BalanceWarehouseDiff;
